import { MouseEvent, useLayoutEffect, useRef, useState } from "react";
import { Flex, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

import { getTagBadges, TTagBadge } from "MVVM/Utilities/ClipTags";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";

type TBadgeSize = "xs" | "sm" | "md";

/* `xs` exists for tight contexts like the branded HLTV playlist tray
   where rows fight for horizontal space at <700px iframe widths. Match-
   history and the rest of the app continue to use `sm`/`md` at their
   original dimensions — `xs` is purely additive. */
const SIZES = {
  xs: { fontSize: 10, padding: "2px 7px", iconSize: 11, weaponIcon: 12 },
  sm: { fontSize: 12, padding: "4px 9px", iconSize: 14, weaponIcon: 16 },
  md: { fontSize: 13, padding: "5px 12px", iconSize: 16, weaponIcon: 18 },
} as const;

/* Branded callers (HLTV tray + IntroOverlay) ride a separate scale than
   the default SIZES table so the chips read as compact campaign tags.
   Match-history is untouched (no `branded` prop).
   Fourth pass: bumped every dimension ~2px up from the previous aggressive
   scale — the proportions (text vs. icon vs. padding) were correct, just
   too small to read comfortably. Heavy 800 weight + all-caps keeps the
   chips compact at these sizes. */
const BRANDED_SIZES = {
  xs: { fontSize: 9, padding: "2px 6px", iconSize: 9, weaponIcon: 10 },
  sm: { fontSize: 10, padding: "2px 7px", iconSize: 11, weaponIcon: 12 },
  md: { fontSize: 11, padding: "3px 9px", iconSize: 13, weaponIcon: 14 },
} as const;

const sizeFor = ($size: TBadgeSize, $branded?: boolean) =>
  ($branded ? BRANDED_SIZES : SIZES)[$size];

interface IClipBadgesProps {
  tags?: { key: string; value: string }[];
  size?: TBadgeSize;
  maxVisible?: number;
  expandable?: boolean;
  /**
   * When provided, each rendered badge becomes a button that calls back
   * with the underlying badge. Caller decides whether to set/clear an
   * external filter, log analytics, or both. Leaving this undefined keeps
   * the original non-interactive rendering (existing call sites unchanged).
   */
  onTagClick?: (badge: TTagBadge) => void;
  /**
   * Label of the currently active filter (matches `TTagBadge.label`, e.g.
   * "ACE", "1V4"). The matching badge renders in a "pressed" visual state
   * so users see which pill drove the current filter. Pure visual hint;
   * no effect when `onTagClick` is not wired.
   */
  activeFilter?: string | null;
  /**
   * Opt-in to campaign brand coloring. When true, every non-ACE badge
   * resolves its fill / border / text from `var(--accent)` (set by the
   * branded HLTV shell) instead of the hardcoded envy green / navy50
   * palette. ACE keeps its gold gradient regardless — gold reads as a
   * "rarity" signal independent of brand color, and the shimmer animation
   * is tuned to that palette.
   *
   * Off by default so match-history and every other existing caller
   * continues to render the original Allstar-green pills unchanged.
   */
  branded?: boolean;
}

const BADGE_GAP = 6;

const ClipBadges = ({
  tags,
  size = "sm",
  maxVisible = 2,
  expandable = false,
  onTagClick,
  activeFilter,
  branded = false,
}: IClipBadgesProps) => {
  const [showAll, setShowAll] = useState(false);
  const tagBadges = getTagBadges(tags);
  const [fitCount, setFitCount] = useState(
    Math.min(tagBadges.length, maxVisible),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (expandable) return; // expandable mode wraps; no width-based truncation
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const compute = () => {
      const cWidth = container.offsetWidth;
      const children = Array.from(measure.children) as HTMLElement[];
      let cumulative = 0;
      let count = 0;
      for (let i = 0; i < children.length; i++) {
        const next =
          cumulative + (i > 0 ? BADGE_GAP : 0) + children[i].offsetWidth;
        if (next > cWidth) break;
        cumulative = next;
        count++;
      }
      return Math.min(count, maxVisible);
    };

    // Skip redundant updates so a recompute that lands on the same value does
    // not trigger an extra render.
    const apply = () =>
      setFitCount((prev) => {
        const next = compute();
        return prev === next ? prev : next;
      });

    // Initial measure is fine synchronously (we're inside a layout effect).
    apply();

    let raf = 0;
    const obs = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    });
    obs.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [tagBadges, maxVisible, expandable]);

  if (tagBadges.length === 0) return null;

  const cap = expandable ? maxVisible : fitCount;
  const visibleBadges = showAll ? tagBadges : tagBadges.slice(0, cap);
  const hasMore = tagBadges.length > cap;

  return (
    <>
      {!expandable && (
        <div
          ref={measureRef}
          aria-hidden="true"
          style={{
            position: "fixed",
            top: -9999,
            left: -9999,
            display: "flex",
            gap: `${BADGE_GAP}px`,
            visibility: "hidden",
            pointerEvents: "none",
          }}
        >
          {tagBadges.map((badge, i) => (
            <ClipBadge key={i} badge={badge} size={size} branded={branded} />
          ))}
        </div>
      )}
      <Flex
        ref={containerRef}
        alignItems="center"
        style={{
          gap: `${BADGE_GAP}px`,
          flexWrap: expandable ? "wrap" : undefined,
          width: expandable ? undefined : "100%",
          minWidth: 0,
          flexShrink: 1,
          overflow: "hidden",
        }}
      >
        {visibleBadges.map((badge, i) => (
          <ClipBadge
            key={i}
            badge={badge}
            size={size}
            onClick={onTagClick}
            isActive={activeFilter === badge.label}
            branded={branded}
          />
        ))}
        {expandable && hasMore && (
          <ViewAllButton onClick={() => setShowAll(!showAll)}>
            {showAll ? "Show Less" : `+${tagBadges.length - cap} More`}
          </ViewAllButton>
        )}
      </Flex>
    </>
  );
};

export { ClipBadges };

interface IClipBadgeProps {
  badge: TTagBadge;
  size: TBadgeSize;
  onClick?: (badge: TTagBadge) => void;
  isActive?: boolean;
  branded?: boolean;
}

const getBadgeWrapper = (badge: TTagBadge) => {
  switch (true) {
    case badge.ace:
      return AceBadgeStyled;
    case badge.highlight:
      return HighlightBadgeStyled;
    case badge.featured:
      return FeaturedBadgeStyled;
    default:
      return null;
  }
};

const ClipBadge = ({
  badge,
  size,
  onClick,
  isActive,
  branded,
}: IClipBadgeProps) => {
  const Icon = badge.icon;
  const Wrapper = getBadgeWrapper(badge);
  // $branded now flows through every variant — including ACE — for
  // sizing + border-radius purposes. Color decoupling for ACE happens
  // inside AceBadgeStyled itself (gold gradient is hardcoded there and
  // ignores $branded), so the dispatch can pass the prop uniformly.
  const useBranded = !!branded;
  const iconSize = sizeFor(size, useBranded).iconSize;
  const clickable = typeof onClick === "function";

  // When made clickable, stop propagation so a parent row's onClick
  // (e.g. "play this clip") doesn't fire alongside the filter toggle.
  const handleClick = (e: MouseEvent) => {
    if (!clickable) return;
    e.stopPropagation();
    onClick?.(badge);
  };

  const interactiveProps = clickable
    ? {
        as: "button" as const,
        type: "button" as const,
        onClick: handleClick,
        $clickable: true,
        $active: !!isActive,
        $branded: useBranded,
      }
    : { $branded: useBranded };

  const content = Wrapper ? (
    <Wrapper $size={size} {...interactiveProps}>
      <Icon size={iconSize} />
      {badge.label}
    </Wrapper>
  ) : (
    <TTagBadgePill $size={size} {...interactiveProps}>
      {badge.iconUrl ? (
        <WeaponIcon
          $size={size}
          $branded={useBranded}
          src={badge.iconUrl}
          alt=""
        />
      ) : (
        <Icon size={iconSize} />
      )}
      {badge.label}
    </TTagBadgePill>
  );

  if (badge.tooltip) {
    return <AnimatedTooltip content={badge.tooltip}>{content}</AnimatedTooltip>;
  }
  return content;
};

const shimmer = keyframes`
  0% { background-position: -100px 0; }
  100% { background-position: 200px 0; }
`;

/* Shared button-reset + interaction surface for the clickable variant.
   Inlined into each styled wrapper below so we don't change layout
   semantics — just removes the <button> defaults (font, border, etc.) and
   adds hover/active treatments. Pressed (filter active) bumps brightness
   and applies a soft ring so users see which pill drove the filter. */
const clickableSurface = `
  appearance: none;
  cursor: pointer;
  font: inherit;
  outline: none;

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  }
`;

const TTagBadgePill = styled(Text)<{
  $size: TBadgeSize;
  $clickable?: boolean;
  $active?: boolean;
  $branded?: boolean;
}>`
  align-items: center;
  background: ${({ theme, $active, $branded }) => {
    if ($active) {
      return $branded ? "var(--accent, #4dbd94)" : theme.colors.envy;
    }
    return $branded ? BRAND_INACTIVE_BG : theme.colors.navy50;
  }};
  border: 1px solid
    ${({ $active, $branded, theme }) => {
      if ($active) {
        return $branded ? "var(--accent, #4dbd94)" : theme.colors.envy;
      }
      return $branded ? BRAND_INACTIVE_BORDER : "transparent";
    }};
  /* Pill-shape (999px) when branded; original 10px soft-rect otherwise
     so match-history weapon pills (AWP/AK47/etc.) read unchanged. */
  border-radius: ${({ $branded }) => ($branded ? "999px" : "10px")};
  color: ${({ theme, $active, $branded }) => {
    if ($active) {
      return $branded ? "var(--accent-text, #0a0a0d)" : theme.colors.navy900;
    }
    return $branded ? "var(--accent, #4dbd94)" : theme.colors.shade200;
  }};
  display: inline-flex;
  /* All typography declarations are wrapped in a && (doubled-class
     selector) to outrank Rebass Text's built-in styles. Without this,
     Rebass's class — emitted into the stylesheet later than our
     styled-components class — wins the cascade on equal specificity,
     which is why prior font-size changes "didn't apply": the icon
     followed our size prop (SVG attribute, not CSS) but the text
     held at the Rebass default. */
  && {
    font-family: Barlow;
    font-size: ${({ $size, $branded }) => sizeFor($size, $branded).fontSize}px;
    /* Branded chips ride 800 (matches Figma's heavy all-caps treatment);
       match-history stays at the original 600. */
    font-weight: ${({ $branded }) => ($branded ? 800 : 600)};
    /* Tighten icon-to-label gap and letter-spacing in branded mode — at
       7-9px the default 4px / 0.5px values are oversized and bloat the
       chip horizontally; match-history keeps the original values. */
    gap: ${({ $branded }) => ($branded ? "3px" : "4px")};
    letter-spacing: ${({ $branded }) => ($branded ? "0.3px" : "0.5px")};
    line-height: 1;
    padding: ${({ $size, $branded }) => sizeFor($size, $branded).padding};
    text-transform: uppercase;
    white-space: nowrap;
  }

  ${({ $clickable }) => $clickable && clickableSurface}
  ${({ $clickable, $branded, theme }) =>
    $clickable &&
    `&:hover {
       background: ${
         $branded ? BRAND_HOVER_BG : theme.colors.navy100 ?? theme.colors.navy50
       };
       border-color: ${
         $branded ? "var(--accent, #4dbd94)" : `${theme.colors.envy}40`
       };
     }`}

  /* The 480px narrow-viewport override only applies to non-branded callers.
     Branded usage runs inside an iframe that's often ≤480px wide — letting
     this fire would inflate branded xs pills back above their 9px scale,
     defeating the compact treatment. Branded callers keep their
     BRANDED_SIZES values at all widths. */
  ${({ $branded }) =>
    !$branded &&
    `@media (max-width: 480px) {
       font-size: 10px;
       padding: 2px 6px;
     }`}
`;

/* ACE keeps its gold gradient regardless of $branded (gold reads as a
   rarity signal and is decoupled from campaign color), but $branded DOES
   drive sizing + border-radius so the ACE pill stays visually consistent
   with the Featured/TTag pills sitting next to it in a branded row. */
const AceBadgeStyled = styled(Text)<{
  $size: TBadgeSize;
  $clickable?: boolean;
  $active?: boolean;
  $branded?: boolean;
}>`
  align-items: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.goldStar},
    ${({ theme }) => theme.colors.timelineGold}
  );
  border: ${({ $active }) => ($active ? "2px solid #fff" : "none")};
  /* Pill-shape (999px) when branded so ACE matches its branded neighbors;
     otherwise the original 10px soft-rect for match-history. */
  border-radius: ${({ $branded }) => ($branded ? "999px" : "10px")};
  color: ${({ theme }) => theme.colors.navy900};
  display: inline-flex;
  overflow: hidden;
  position: relative;
  text-shadow: 0 1px 2px rgba(255, 215, 0, 0.2);
  white-space: nowrap;

  /* The doubled-class && selector outranks Rebass Text's built-in
     typography styles — see TTagBadgePill for the full rationale.
     Without it our font-size/padding don't apply on top of Rebass. */
  && {
    font-family: Barlow;
    font-size: ${({ $size, $branded }) => sizeFor($size, $branded).fontSize}px;
    font-weight: 800;
    /* Branded mode tightens gap + tracking to match the Featured/TTag
       branded chips it sits next to. */
    gap: ${({ $branded }) => ($branded ? "3px" : "4px")};
    letter-spacing: ${({ $branded }) => ($branded ? "0.3px" : "0.8px")};
    line-height: 1;
    padding: ${({ $size, $branded }) => sizeFor($size, $branded).padding};
    text-transform: uppercase;
  }

  ${({ $clickable }) => $clickable && clickableSurface}

  &::after {
    animation: ${shimmer} 5s ease-in-out infinite;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 100%
    );
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 80px;
  }

  /* Skip the 480px inflation override for branded mode — same rationale
     as the other variants (branded chips run inside narrow iframes and
     the override would defeat the compact scale). */
  ${({ $branded }) =>
    !$branded &&
    `@media (max-width: 480px) {
       font-size: 10px;
       padding: 2px 6px;
     }`}
`;

/* Brand-color mix recipe shared across the branded pill variants.
   - Inactive fill:   28% accent in transparent  (heavier than the 12%
                      envy20 default, matching Figma's filled chip weight)
   - Inactive border: 50% accent in transparent
   - Inactive text:   var(--accent) — full saturation reads cleanly on
                      the tinted-transparent fill
   - Active (filter pressed):
       fill   = var(--accent)
       border = var(--accent)
       text   = var(--accent-text) — set by BrandedShell to the proper
                contrast color for the campaign (white on most brands,
                dark on yellow/lime brands).
   Match-history doesn't pass `$branded`, so its pills resolve through
   the original envy/navy theme path unchanged. */
const BRAND_INACTIVE_BG =
  "color-mix(in srgb, var(--accent, #4dbd94) 28%, transparent)";
const BRAND_INACTIVE_BORDER =
  "color-mix(in srgb, var(--accent, #4dbd94) 50%, transparent)";
const BRAND_HOVER_BG =
  "color-mix(in srgb, var(--accent, #4dbd94) 40%, transparent)";

const FeaturedBadgeStyled = styled(Text)<{
  $size: TBadgeSize;
  $clickable?: boolean;
  $active?: boolean;
  $branded?: boolean;
}>`
  align-items: center;
  background: ${({ theme, $active, $branded }) => {
    if ($active) {
      return $branded ? "var(--accent, #4dbd94)" : theme.colors.envy;
    }
    return $branded ? BRAND_INACTIVE_BG : `${theme.colors.envy}20`;
  }};
  border: 1px solid
    ${({ theme, $active, $branded }) => {
      if ($active) {
        return $branded ? "var(--accent, #4dbd94)" : theme.colors.envy;
      }
      return $branded ? BRAND_INACTIVE_BORDER : `${theme.colors.envy}40`;
    }};
  /* Branded pills go fully-rounded (999px clamps to pill) per Figma; the
     default 10px softened-rect is preserved for match-history. */
  border-radius: ${({ $branded }) => ($branded ? "999px" : "10px")};
  color: ${({ theme, $active, $branded }) => {
    if ($active) {
      return $branded ? "var(--accent-text, #0a0a0d)" : theme.colors.navy900;
    }
    return $branded ? "var(--accent, #4dbd94)" : theme.colors.envy;
  }};
  display: inline-flex;
  /* The && doubled-class selector boosts specificity so our font/text
     declarations outrank Rebass Text's built-in styles — see
     TTagBadgePill above for the full rationale. (Without this, Rebass's
     class wins the cascade and our font-size changes look like no-ops,
     even though iconSize works because it's a prop on the SVG, not CSS.) */
  && {
    font-family: Barlow;
    /* Branded callers use the compacter BRANDED_SIZES scale; match-
       history stays on the original SIZES table via sizeFor's fallback. */
    font-size: ${({ $size, $branded }) => sizeFor($size, $branded).fontSize}px;
    /* Heavier weight (800) in branded mode to match Figma's bold all-caps
       pill type; match-history retains its original 600. */
    font-weight: ${({ $branded }) => ($branded ? 800 : 600)};
    /* Tighter gap + letter-spacing in branded mode; see
       FeaturedBadgeStyled for rationale. */
    gap: ${({ $branded }) => ($branded ? "3px" : "4px")};
    letter-spacing: ${({ $branded }) => ($branded ? "0.3px" : "0.5px")};
    line-height: 1;
    padding: ${({ $size, $branded }) => sizeFor($size, $branded).padding};
    text-transform: uppercase;
    white-space: nowrap;
  }

  ${({ $clickable }) => $clickable && clickableSurface}
  ${({ $clickable, $branded, theme }) =>
    $clickable &&
    `&:hover {
       background: ${$branded ? BRAND_HOVER_BG : `${theme.colors.envy}35`};
     }`}

  /* Same rationale as TTagBadgePill: skip the 480px inflation override
     for branded mode so the BRANDED_SIZES values hold inside narrow
     branded iframes. */
  ${({ $branded }) =>
    !$branded &&
    `@media (max-width: 480px) {
       font-size: 10px;
       padding: 2px 6px;
     }`}
`;

const HighlightBadgeStyled = styled(FeaturedBadgeStyled)`
  overflow: hidden;
  position: relative;

  &::after {
    animation: ${shimmer} 5s ease-in-out infinite;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(77, 189, 148, 0.15) 50%,
      transparent 100%
    );
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 200%;
  }
`;

const WeaponIcon = styled.img<{ $size: TBadgeSize; $branded?: boolean }>`
  filter: brightness(0) invert(0.75);
  height: ${({ $size, $branded }) => sizeFor($size, $branded).weaponIcon}px;
  margin: -1px 0;
  object-fit: contain;
  width: ${({ $size, $branded }) => sizeFor($size, $branded).weaponIcon}px;
`;

const ViewAllButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.navy200};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  padding: 5px 12px;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};
  }
`;
